export const CLINIC_TIMEZONE = "America/Argentina/Buenos_Aires";

const partsFormatter = new Intl.DateTimeFormat("en-US", {
  timeZone: CLINIC_TIMEZONE,
  hourCycle: "h23",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
});

function zonedParts(date: Date) {
  const out: Record<string, number> = {};
  for (const p of partsFormatter.formatToParts(date)) {
    if (p.type !== "literal") out[p.type] = Number(p.value);
  }
  return out as { year: number; month: number; day: number; hour: number; minute: number; second: number };
}

function offsetMinutesAt(date: Date) {
  const p = zonedParts(date);
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  return Math.round((asUtc - date.getTime()) / 60000);
}

/** Local clinic date ("YYYY-MM-DD") + minutes from local midnight → UTC Date. */
export function clinicTimeToUtc(localDate: string, minutesFromMidnight: number): Date {
  const [y, m, d] = localDate.split("-").map(Number) as [number, number, number];
  const guess = new Date(Date.UTC(y, m - 1, d, 0, minutesFromMidnight));
  const offset = offsetMinutesAt(guess);
  return new Date(guess.getTime() - offset * 60000);
}

export function toClinicDate(date: Date): string {
  const p = zonedParts(date);
  return `${p.year}-${String(p.month).padStart(2, "0")}-${String(p.day).padStart(2, "0")}`;
}

export function clinicMinutesOfDay(date: Date): number {
  const p = zonedParts(date);
  return p.hour * 60 + p.minute;
}

/** ISO weekday in clinic timezone: 1 = Monday … 7 = Sunday. */
export function clinicWeekday(localDate: string): number {
  const day = clinicTimeToUtc(localDate, 12 * 60);
  const wd = new Intl.DateTimeFormat("en-US", { timeZone: CLINIC_TIMEZONE, weekday: "short" }).format(day);
  return ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].indexOf(wd) + 1;
}

export function addDays(localDate: string, days: number): string {
  const [y, m, d] = localDate.split("-").map(Number) as [number, number, number];
  const t = new Date(Date.UTC(y, m - 1, d + days));
  return t.toISOString().slice(0, 10);
}

export function formatClinicTime(date: Date | string, locale = "es-AR") {
  return new Intl.DateTimeFormat(locale, { timeZone: CLINIC_TIMEZONE, hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).format(
    new Date(date),
  );
}

export function formatClinicDate(date: Date | string, opts: Intl.DateTimeFormatOptions = {}, locale = "es-AR") {
  return new Intl.DateTimeFormat(locale, {
    timeZone: CLINIC_TIMEZONE,
    weekday: "long",
    day: "numeric",
    month: "long",
    ...opts,
  }).format(new Date(date));
}

export function minutesToHHMM(minutes: number) {
  return `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;
}

export function hhmmToMinutes(hhmm: string) {
  const [h, m] = hhmm.split(":").map(Number) as [number, number];
  return h * 60 + m;
}
